<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import draggable from 'vuedraggable';
import { usePipelineStore } from '../store/usePipeline';
import LeadDrawer from '../components/LeadDrawer.vue';
import NewLeadModal from '../components/NewLeadModal.vue';
import PipelineConfigModal from '../components/PipelineConfigModal.vue';
import RelatoriosModal from '../components/RelatoriosModal.vue';
import { crmmFeatures as __feat, loadCrmmFeatures as __loadFeat } from 'dashboard/helper/crmmFeatures';
const route = useRoute();
const store = usePipelineStore();
const accountId = computed(() => route.params.accountId);
// Relatorios = so admin/dono (gate por role vindo do /crmm/config; atendente nem ve o botao)
const isAdmin = computed(() => !!(__feat.value && __feat.value.is_admin));
const cols = ref([]);
const selected = ref(null);
const newModal = ref(null);
const cfgModal = ref(null);
const fmt = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
// roda do mouse sobre o board = scroll LATERAL (padrao kanban); coluna com overflow vertical proprio mantem o scroll normal
const onBoardWheel = (e) => {
  if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
  let el = e.target;
  while (el && el !== e.currentTarget) { if (el.scrollHeight > el.clientHeight + 2) return; el = el.parentElement; }
  e.currentTarget.scrollLeft += e.deltaY; e.preventDefault();
};
const rebuild = () => { cols.value = store.stages.map((s) => Object.assign({}, s, { leads: store.leads.filter((l) => l.stage_id === s.id) })); };
const pid = ref('');
const load = async () => { await store.loadBoard(accountId.value, pid.value); pid.value = store.currentPipelineId; rebuild(); };
const switchFunil = async () => { await load(); };
const creating = ref(false); const novoNome = ref('');
const criarFunil = async () => { if (!novoNome.value.trim()) { creating.value = false; return; } const r = await store.createPipeline(accountId.value, novoNome.value.trim()); novoNome.value = ''; creating.value = false; if (r && r.id) { pid.value = r.id; await load(); } };
const scoreColor = (s) => s == null ? '' : (s >= 67 ? '#5fe0a0' : (s >= 34 ? '#f5a623' : '#ff6b6b'));
const colTotal = (c) => c.leads.reduce((a, l) => a + Number(l.valor || 0), 0);
const lostPrompt = ref(null); const lostMotivo = ref(''); const relModal = ref(null);
const onChange = (evt, stageId) => {
  if (!evt || !evt.added) return;
  const lead = evt.added.element;
  const col = cols.value.find((c) => c.id === stageId);
  if (col && col.is_lost) { lostMotivo.value = ''; lostPrompt.value = { leadId: lead.id, stageId }; }
  else { store.moveLead(lead.id, stageId); }
};
const confirmLost = (skip) => { const p = lostPrompt.value; if (p) store.moveLead(p.leadId, p.stageId, skip ? '' : lostMotivo.value); lostPrompt.value = null; };
onMounted(async () => { __loadFeat(); await store.loadPipelines(accountId.value); await load(); });
</script>

<template>
  <div class="flex flex-col h-full bg-n-background text-n-slate-12 p-4 overflow-hidden relative">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-3">
        <h1 class="text-xl font-semibold text-n-slate-12">Funil</h1>
        <select v-if="store.pipelines.length" v-model="pid" class="bg-n-solid-2 text-n-slate-12 text-sm rounded-lg px-2 py-1.5 outline outline-1 outline-n-weak" @change="switchFunil">
          <option v-for="p in store.pipelines" :key="p.id" :value="p.id">{{ p.nome }}</option>
        </select>
        <template v-if="creating">
          <input v-model="novoNome" placeholder="Nome do funil" class="bg-n-alpha-black2 text-n-slate-12 text-sm rounded-lg px-2 py-1.5 outline outline-1 outline-n-brand" @keyup.enter="criarFunil" />
          <button class="text-xs px-2 py-1.5 rounded-lg bg-n-brand text-white" @click="criarFunil">Criar</button>
        </template>
        <button v-else class="text-xs px-2 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-alpha-2" @click="creating = true">+ Funil</button>
      </div>
      <div class="flex items-center gap-2">
        <button class="text-xs px-3 py-1.5 rounded-lg bg-n-brand text-white hover:opacity-90" @click="newModal && newModal.open()">+ Novo lead</button>
        <button v-if="isAdmin" class="text-xs px-3 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-alpha-2" @click="relModal && relModal.open()">Relatorios</button>
        <button class="text-xs px-3 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-alpha-2" @click="cfgModal && cfgModal.open()">Estagios</button>
        <button class="text-xs px-3 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-alpha-2" @click="load">Atualizar</button>
      </div>
    </div>
    <div v-if="store.loading" class="text-n-slate-11">Carregando board...</div>
    <div v-else-if="store.error" class="text-n-ruby-11">Erro: {{ store.error }}</div>
    <div v-else class="flex gap-3 overflow-x-auto flex-1 pb-2" @wheel="onBoardWheel">
      <div v-for="c in cols" :key="c.id" class="flex flex-col w-72 shrink-0 bg-n-solid-2 rounded-xl outline outline-1 outline-n-container p-2 max-h-full">
        <div class="flex items-center justify-between px-1 py-2 border-b border-n-weak mb-2">
          <span class="font-semibold text-sm flex items-center gap-2"><span class="w-2 h-2 rounded-full" :style="{ background: c.cor }"></span>{{ c.nome }}</span>
          <span class="text-xs text-n-slate-11">{{ c.leads.length }} &middot; {{ fmt(colTotal(c)) }}</span>
        </div>
        <draggable :list="c.leads" group="cards" item-key="id" class="flex flex-col gap-2 overflow-y-auto flex-1 min-h-[48px]" ghost-class="funil-ghost" :animation="180" @change="onChange($event, c.id)">
          <template #item="{ element }">
            <div class="bg-n-solid-1 rounded-lg p-2.5 outline outline-1 outline-n-weak cursor-grab active:cursor-grabbing hover:outline-n-strong" @click="selected = element">
              <div class="flex items-start justify-between gap-2">
                <div class="text-sm font-medium text-n-slate-12">{{ element.nome || ('Lead ' + element.contact_chatwoot_id) }}</div>
                <span v-if="element.score != null" class="text-[10px] font-bold px-1.5 py-0.5 rounded-md shrink-0" :style="{ background: scoreColor(element.score) + '33', color: scoreColor(element.score) }">{{ element.score }}</span>
              </div>
              <div class="text-xs text-n-slate-11 mt-0.5">{{ element.origem || '-' }} &middot; {{ fmt(element.valor) }}</div>
            </div>
          </template>
        </draggable>
        <div v-if="!c.leads.length" class="text-xs text-n-slate-10 px-1 py-2 text-center">Sem leads</div>
      </div>
    </div>
    <LeadDrawer :lead="selected" :stages="store.stages" :account-id="accountId" @close="selected = null" @changed="load" />
    <NewLeadModal ref="newModal" :stages="store.stages" :account-id="accountId" @created="load" />
    <PipelineConfigModal ref="cfgModal" :stages="store.stages" @saved="load" />
    <RelatoriosModal ref="relModal" />
    <div v-if="lostPrompt" class="fixed inset-0 z-30 flex items-center justify-center bg-n-alpha-black1" @click.self="confirmLost(true)">
      <div class="w-[380px] max-w-[90vw] bg-n-solid-2 rounded-xl outline outline-1 outline-n-container shadow-xl p-4 flex flex-col gap-3">
        <div class="text-sm font-medium text-n-slate-12">Motivo da perda</div>
        <input v-model="lostMotivo" placeholder="Ex: preco, timing, concorrente..." class="w-full bg-n-alpha-black2 rounded-lg px-2 py-1.5 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand" @keyup.enter="confirmLost(false)" />
        <div class="flex gap-2 justify-end">
          <button class="text-sm px-3 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12" @click="confirmLost(true)">Pular</button>
          <button class="text-sm px-3 py-1.5 rounded-lg bg-n-brand text-white" @click="confirmLost(false)">Salvar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.funil-ghost { opacity: 0.4; }
</style>
